import { useState, useRef, useEffect} from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../store/store";
import { fetchStatusOptions, getTuitionAdminDocuments,fetchActiveStatusOptions, fetchEmailOptions, patchTuitionAdminBulkStatus, patchTuitionAdminUploadEeFlag, searchTuitionApplications } from "../../../../store/slices/Tuition/tuitionAdminSlice";
import DetailsTable from "./DetailsTable";
import DetailsTabsContainer from "./DetailsTabsContainer";
import BreadCrumbs from "../../../../components/common/BreadCrumbs/BreadCrumbs";
import Dropdown from "../../../../components/common/AdminDropdown";
import Loader from "../../../../components/common/Loader";
import Notifications from "../../../../components/common/Notifications";
import { useNavigate } from "react-router-dom";
import TuitionSearchStatus from "./TuitionSearchStatus";
import { trackButtonClick, trackNavigation } from '../../../../services/analytics';
import { safeGet } from "../../../../utils/safeGet";

type ManagerAcceptedFailure = {
  applicationId: string;
  reason: string;
};

const isManagerApprovalFailure = (reason: string): boolean => {
  const normalizedReason = reason.toLowerCase();
  return normalizedReason.includes("manager")
    && (normalizedReason.includes("approved") || normalizedReason.includes("accepted"));
};


const TuitionContainer = () => {
  const dispatch = useDispatch<AppDispatch>();

  // Map displayed header text to DB field names
  const mapHeaderToField = (label: string) => {
    const normalized = String(safeGet(label, '')).toLowerCase().replaceAll(/\s+/g, ' ').trim();
    switch (normalized) {
      case 'employee nuid':
        return 'EMPLOYEE_TXN.EMP_NUID';
      case 'employee id':
        return 'EMPL_APPLICTN.EMP_ID';
      case 'last name':
        return 'EMPLOYEE.EMP_LAST_NAME';
      case 'first name':
        return 'EMPLOYEE.EMP_FIRST_NAME';
      case 'application id':
        return 'EMPL_APPLICTN.APPLICTN_ID';
      case 'program type':
        return 'EMPL_APPLICTN.PROG_TYPE';
      case 'region':
        return 'EMPL_APPLICTN.REGION_ID';
      case 'status':
        return 'APPLICTN_STATUS_TYPE.APP_STATUS';
      case 'employee upload flag':
        return 'DOCUMENT.EMP_UPLOAD_FLAG';
      case 'term begin date':
        return 'EMPL_APPLICTN.TERM_BEGIN';
      default:
        return 'UNKNOWN_FIELD';
    }
  };

  
  const { applications, activeStatusOptions, emailOptions } = useSelector((state: RootState) => state.tuitionAdmin);
   const detailsTabsRef = useRef<HTMLDivElement>(null);
  const [selectedStatus, setSelectedStatus] = useState("Select Status");
  const [showSearchPanel, setShowSearchPanel] = useState(true);
  const [showResults, setShowResults] = useState(false);
  const [selectedApplicationData, setSelectedApplicationData] = useState<any>(null);
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchParams, setSearchParams] = useState<any>(null);
  // Keep a ref to the latest searchParams so event listeners (created once) can read fresh values
  const searchParamsRef = useRef<any>(searchParams);
  useEffect(() => {
    searchParamsRef.current = searchParams;
  }, [searchParams]);
  const [selectedAction, setSelectedAction] = useState("Select action");
  const [selectedFilterValue, setSelectedFilterValue] = useState("Select filter value");
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [checkedRowIds, setCheckedRowIds] = useState<string[]>([]);
  const [actionError, setActionError] = useState<string>("");  
  const [clearSelectionTrigger, setClearSelectionTrigger] = useState(0);
  const [isFetchingActiveStatuses, setIsFetchingActiveStatuses] = useState(false);
  const [documentsRefreshTrigger, setDocumentsRefreshTrigger] = useState(0);
  const [successNotification, setSuccessNotification] = useState<string>("");
  const [errorNotification, setErrorNotification] = useState<string>("");
  const [warningMessage, setWarningMessage] = useState<string>("");
  const [failedApplications, setFailedApplications] = useState<string[]>([]);
  const [managerAcceptedFailures, setManagerAcceptedFailures] = useState<ManagerAcceptedFailure[]>([]);

  const [checkedRows, setCheckedRows] = useState<string[]>([]);
 
  const [isSorting, setIsSorting] = useState(false);

  const navigate = useNavigate();

  const detailsTableWrapperRef = useRef<HTMLDivElement | null>(null);

  // Persist chevron sort state across re-renders / API calls.
  // Keyed by mappedField (e.g. 'EMPLOYEE_TXN.EMP_NUID') -> 'ASC' | 'DESC'
  const sortStateRef = useRef<Record<string, 'ASC' | 'DESC'>>({});
  // Track which field currently has an active sort icon so others can be reset
  const activeSortFieldRef = useRef<string | null>(null);

  const computeHeaderLabel = (hdr: HTMLElement) => {
    const wrapper = hdr.querySelector('.__header-content-wrapper');
    const raw = safeGet(wrapper?.textContent, safeGet(hdr.textContent, ''));
    return raw.replace(/chevron\s*(down|up)/i, '').trim();
  };

  // Helper: reset other chevrons to down state
  const resetOtherChevrons = (mappedField: string, chevronDownUrl: string) => {
    const rootEl = detailsTableWrapperRef.current;
    if (!rootEl) return;
    const others = Array.from(rootEl.querySelectorAll<HTMLImageElement>('.drop-down-menu-chevron'));
    others.forEach((otherImg) => {
      const parent = otherImg.parentElement;
      if (!parent) return;
      const otherLabel = computeHeaderLabel(parent);
      const otherField = mapHeaderToField(otherLabel);
      if (otherField !== mappedField) {
        otherImg.src = chevronDownUrl;
        otherImg.alt = 'chevron down';
        sortStateRef.current[otherField] = 'DESC';
      }
    });
  };

  // Factory: create toggle handler for a specific header/img
  const createToggle = (
    header: HTMLElement,
    img: HTMLImageElement,
    mappedField: string,
    chevronUpUrl: string,
    chevronDownUrl: string
  ) => {
    return async () => {
      const headerLabel = computeHeaderLabel(header);
      const isDown = img.src.includes('chevrondown.svg');

      // Reset other chevrons immediately
      resetOtherChevrons(mappedField, chevronDownUrl);

      // Toggle the clicked chevron
      img.src = isDown ? chevronUpUrl : chevronDownUrl;
      img.alt = isDown ? 'chevron up' : 'chevron down';
      const direction = isDown ? 'ASC' : 'DESC';

      // Store attributes used by other code
      header.dataset.field = mappedField;
      header.dataset.sort = direction;

      // Persist current active field and its direction
      activeSortFieldRef.current = mappedField;
      sortStateRef.current[mappedField] = direction;

      header.dispatchEvent(new CustomEvent('tuitionHeaderSort', { detail: { field: mappedField, direction, label: headerLabel } }));
      setIsSorting(true);
      try {
        // read the latest params from ref to avoid stale closure
        const currentParams = searchParamsRef.current;
        const baseParams = (currentParams && typeof currentParams === 'object' && !Array.isArray(currentParams))
          ? currentParams
          : { applType: 'AT001' };
        if (Array.isArray(currentParams)) {
          console.warn('searchParamsRef.current is an array, falling back to default base params', currentParams);
        }
        const updatedParams = { ...baseParams, sortOrder: direction, sortBy: mappedField };
        const apiParams = { ...updatedParams };
        // Remove __rawSearch before calling the API
        if (apiParams && typeof apiParams === 'object' && apiParams.__rawSearch) {
          delete apiParams.__rawSearch;
        }
        const resultAction = await dispatch(searchTuitionApplications(apiParams) as any);
        if (searchTuitionApplications.fulfilled.match(resultAction)) {
          const payload = resultAction.payload;
          const data = payload?.data ?? (Array.isArray(payload) ? payload : []);
          setSearchResults(Array.isArray(data) ? data : []);
          setSearchParams(updatedParams);
        } else {
          console.error('Failed to load sorted applications', safeGet(resultAction.payload, resultAction.error));
        }
      } catch (err) {
        console.error('Failed to dispatch sorted search thunk:', err);
      } finally {
        setIsSorting(false);
      }
    };
  };

  // Factory: create keydown handler for accessibility (Enter or Space)
  const createKeydownHandler = (toggleFn: () => Promise<void> | void) => {
    return (e: KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        // call toggle and ignore returned promise
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        toggleFn();
      }
    };
  };

  // Factory: create error handler for an image
  const createImageErrorHandler = (img: HTMLImageElement) => {
    return () => console.error('Failed to load injected chevron:', img.src);
  };

  useEffect(() => {
    // MutationObserver approach: wait until headers are present then inject chevrons once
    const root = detailsTableWrapperRef.current;
    if (!root || !showResults) return;

    const headerSelectors = 'th, [role="columnheader"], .ds-table__header, .table-header, .header, .column-header';
    const baseUrl = safeGet((import.meta as any).env?.BASE_URL, '/');
    const chevronUpUrl = `${baseUrl}assets/images/chevronup.svg`;
    const chevronDownUrl = `${baseUrl}assets/images/chevrondown.svg`;

    const injectChevrons = (headers: NodeListOf<HTMLElement>) => {
      const injected: HTMLImageElement[] = [];
      Array.from(headers).forEach((header, index) => {
        try {
          if (index === 0 || index === headers.length - 1) return;
          if (header.querySelector('.drop-down-menu-chevron')) return;

          const headerLabel = computeHeaderLabel(header);
          const mappedField = mapHeaderToField(headerLabel);

          // minimal positioning so chevron flows inline after header text
          header.style.setProperty('position', (header.style.position, 'relative'), 'important');
          header.style.setProperty('padding', (header.style.padding, '16px 0px 16px 10px'), 'important');

          const img = document.createElement('img');
          img.className = 'drop-down-menu-chevron';
          const stored = sortStateRef.current[mappedField];
          const chosenUrl = stored === 'ASC' ? chevronUpUrl : chevronDownUrl;
          img.alt = stored === 'ASC' ? 'chevron up' : 'chevron down';
          img.src = chosenUrl;
          img.style.display = 'inline-block';
          img.style.marginLeft = '1px';
          img.style.marginRight = '15px';
        
          const toggle = createToggle(header, img, mappedField, chevronUpUrl, chevronDownUrl);
          img.addEventListener('click', toggle);
          img.addEventListener('keydown', createKeydownHandler(toggle));
          img.addEventListener('error', createImageErrorHandler(img));

          header.appendChild(img);
          injected.push(img);
        } catch (e) {
          console.error('Error injecting chevron into header:', e);
        }
      });
      return injected;
    };

    const observer = new MutationObserver((_mutations, obs) => {
      const headers = root.querySelectorAll<HTMLElement>(headerSelectors);
      if (!headers || headers.length === 0) return;
      // inject once and disconnect
      injectChevrons(headers);
      obs.disconnect();
    });

    observer.observe(root, { childList: true, subtree: true });

    // Fallback: if headers are already present or never trigger mutation, run after a short delay
    const fallbackTimer = globalThis.setTimeout(() => {
      const headers = root.querySelectorAll<HTMLElement>(headerSelectors);
      if (headers && headers.length > 0) {
        injectChevrons(headers);
        observer.disconnect();
      }
    }, 800);

    return () => {
      observer.disconnect();
      clearTimeout(fallbackTimer);
    };
  }, [showResults, searchResults, clearSelectionTrigger, showEmailForm]);

  useEffect(() => {
    dispatch(fetchStatusOptions("AT001"));
  }, [dispatch]);

  useEffect(() => {
      setSearchResults(applications);
  }, [applications]);
// CLEAR DETAILS WHEN RESULTS CHANGE
useEffect(() => {
  setSelectedApplicationData(null);
}, [searchResults]);

// CLEAR DETAILS WHEN SEARCH PARAMS CHANGE
useEffect(() => {
  setSelectedApplicationData(null);
}, [searchParams]);

  // Auto-dismiss success notification after 5 seconds
  useEffect(() => {
    if (successNotification) {
      const timer = setTimeout(() => {
        setSuccessNotification("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successNotification]);

  // Auto-dismiss error notification after 5 seconds
  useEffect(() => {
    if (errorNotification) {
      const timer = setTimeout(() => {
        setErrorNotification("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [errorNotification]);

  // Auto-dismiss status failure banners after 25 seconds
  useEffect(() => {
    if (warningMessage || managerAcceptedFailures.length > 0) {
      const timer = setTimeout(() => {
        setWarningMessage("");
        setFailedApplications([]);
        setManagerAcceptedFailures([]);
      }, 25000);
      return () => clearTimeout(timer);
    }
  }, [warningMessage, managerAcceptedFailures]);

  const getActionDropdownOptions = () => {
    if (selectedAction === "Change status") {
      return ["Select filter value", ...activeStatusOptions];
    } else if (selectedAction === "Send email to") {
      // Use dynamic email options from API
      const emailValues = safeGet(emailOptions?.emails?.map((email: any) => email.value), []);
      return ["Select filter value", ...emailValues];
    }
    return ["Select filter value"];
  };

  const handleStatusChange = (status: string) => {
    setSelectedStatus(status);
  };

  const handleViewResults = (results: any[], params: any) => {
    setSearchResults(results); // Store the API results

    // Ensure params is an object (not accidentally the results array). Coerce fallback if necessary.
    let safeParams: any = params;
    if (Array.isArray(params)) {
      console.warn('handleViewResults received params as an Array — falling back to default search params', params);
      safeParams = { applType: 'AT001' };
    } else if (!params || typeof params !== 'object') {
      console.warn('handleViewResults received invalid params — falling back to default search params', params);
      safeParams = { applType: 'AT001' };
    }

    setSearchParams(safeParams); // Store the search params for pagination
    setCheckedRows([]);
    setCheckedRowIds([]);
    setClearSelectionTrigger((prev) => prev + 1);
    setShowResults(true);
    setShowSearchPanel(false);
  };

  const handleShowSearch = () => {
    setShowSearchPanel(true);
  };



  const handleViewDetails = (rowData: any) => {
    console.log(`📊 Analytics: View details clicked - Application ${rowData?.applicationId}`)
    trackButtonClick('View details', 'tuition-admin-container', { applicationId: rowData?.applicationId })
    // trackCustomPageView(`Tuition Admin Details - Application ${rowData?.applicationId}`)
    setSelectedApplicationData(rowData);
     // Scroll to details tabs section after state update
    setTimeout(() => {
      detailsTabsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const handleCheckedRowsChange = (rows: string[]) => {
    setCheckedRows(rows);
    setCheckedRowIds(rows);
    // Clear error when rows are selected
    if (rows.length > 0) {
      setActionError("");
    }
    
    // Reset all dropdowns when no checkboxes are selected
    if (rows.length === 0) {
      setSelectedAction("Select action");
      setSelectedFilterValue("Select filter value");
      setShowFilterDropdown(false);
    }
  };

  const handlePreviousClick = () => {
    console.log('📊 Analytics: Previous clicked - navigating to admin')
    trackButtonClick('Previous', 'tuition-admin-container')
    trackNavigation('/admin/tuition', '/admin', 'back')
    navigate("/admin");
  };

  const handleCreateNewApplication = () => {
    console.log('📊 Analytics: Create new application clicked')
    trackButtonClick('Create new application', 'tuition-admin-container')
    trackNavigation('/admin/tuition', '/admin/tuition/create-application', 'next')
    navigate("/admin/tuition/create-application");
  };

  const handleActionSelect = async (action: string) => {
    console.log(`📊 Analytics: Action selected - ${action}`)
    trackButtonClick(`Action: ${action}`, 'tuition-admin-container', { action })
    setSelectedAction(action);
    setActionError(""); // Clear error when action changes
    
    // Show filter dropdown when any action other than "Select action" and "EE Upload" is selected
    if (action !== "Select action" && action !== "EE Upload") {
      setShowFilterDropdown(true);
      setSelectedFilterValue("Select filter value"); // Reset filter value when action changes
    } else {
      setShowFilterDropdown(false);
      setSelectedFilterValue("Select filter value");
    }
    
    switch (action) {
      case "Filter records":
        break;
      case "Change status":
        setIsFetchingActiveStatuses(true);
        try {
          await dispatch(fetchActiveStatusOptions("AT001")).unwrap();
        } catch (error) {
          console.error("Failed to fetch active status options:", error);
        } finally {
          setIsFetchingActiveStatuses(false);
        }
        break;
      case "Change value":
        break;
      case "Send email to":
        // Fetch email options
        dispatch(fetchEmailOptions("AT001"));
        break;
      case "EE Upload":
        break;
      default:
        break;
    }
  };

  const handleFilterValueSelect = (value: string) => {
    setSelectedFilterValue(value);
  };

  const handleApplyEmailAction = () => {
    // Check if any rows are selected
    if (checkedRowIds.length === 0) {
      setActionError("Please select at least one employee from the table");
      return;
    }
    
    // Clear any previous errors
    setActionError("");
    
    // Show email form - per-application emails are now collected in DetailsTable
    if (selectedAction === "Send email to" && selectedFilterValue !== "Select filter value") {
      setShowEmailForm(true);
    } else if (selectedAction === "Filter records" && selectedFilterValue !== "Select filter value") {
      // Apply filter logic
    }
  };

  const handleCancelEmail = () => { 
    setShowEmailForm(false);
    setSelectedAction("Select action");
    setSelectedFilterValue("Select filter value");
    setShowFilterDropdown(false);
  };

  const showStatusNotification = (response: any) => {
   if (response.failed && response.failed.length > 0) {
    const processedCount = safeGet(response.processed?.length, 0);
     const managerAcceptedFailed: ManagerAcceptedFailure[] = [];
     const otherFailedIds: string[] = [];

     if (Array.isArray(response?.failedDetails) && response.failedDetails.length > 0) {
       response.failedDetails.forEach((item: any) => {
         const applicationId = String(item?.applicationId ?? item?.applId ?? item?.id ?? "").trim();
         const reason = String(item?.reason ?? "").trim();

         if (!applicationId) {
           return;
         }

         if (isManagerApprovalFailure(reason)) {
           managerAcceptedFailed.push({ applicationId, reason });
         } else {
           otherFailedIds.push(applicationId);
         }
       });
     } else {
       otherFailedIds.push(...response.failed.map((applicationId: any) => String(applicationId).trim()));
     }

     setSuccessNotification(
       processedCount > 0
         ? `Status updated successfully for ${processedCount} selected application(s).`
         : ""
     );
     setErrorNotification("");
     setManagerAcceptedFailures(managerAcceptedFailed);

     if (otherFailedIds.length > 0) {
       setFailedApplications(otherFailedIds);
       setWarningMessage(
         `Warning Status was not updated for the following applications`
       );
     } else {
       setFailedApplications([]);
       setWarningMessage("");
     }
   } else {
     setSuccessNotification(
       `Status updated successfully for ${safeGet(response.processed?.length, checkedRows.length)} selected application(s).`
     );
     setErrorNotification("");
     setWarningMessage("");
     setFailedApplications([]);
     setManagerAcceptedFailures([]);
   }
  };

const applyChangeStatus = async () => {
  if (checkedRows.length === 0 || selectedFilterValue === "Select filter value") {
    setErrorNotification("Please select applications and a valid status.");
    return;
  }

  setSuccessNotification("");
  setErrorNotification("");
  setWarningMessage("");
  setFailedApplications([]);
  setManagerAcceptedFailures([]);

  const action = await dispatch(
    patchTuitionAdminBulkStatus({
      applicationIds: checkedRows.map(String),
      newStatus: selectedFilterValue,
    }) as any
  );

  if ((patchTuitionAdminBulkStatus as any).fulfilled.match(action)) {
    const response = action.payload;

    if (searchParams) {
      const apiParamsForRefresh = { ...(typeof searchParams === 'object' && !Array.isArray(searchParams) ? searchParams : { applType: 'AT001' }) };
      if (apiParamsForRefresh.__rawSearch) delete apiParamsForRefresh.__rawSearch;
      await dispatch(searchTuitionApplications(apiParamsForRefresh) as any);
    }

    setSelectedAction("Select action");
    setSelectedFilterValue("Select filter value");
    setShowFilterDropdown(false);
    setCheckedRows([]);
    setClearSelectionTrigger((prev) => prev + 1);
    setDocumentsRefreshTrigger((prev) => prev + 1);

    showStatusNotification(response);
  } else {
    setErrorNotification(
      typeof action.payload === "string" && action.payload.trim()
        ? action.payload
        : "Failed to update status. Please try again."
    );
  }
};

  const applyEeUpload = async () => {
    if (checkedRows.length === 0) {
      setErrorNotification("Please select applications to upload EE flag.");
      return;
    }
    const action = await dispatch(
      patchTuitionAdminUploadEeFlag({
        applicationIds: checkedRows.map(String),
      }) as any
    );
    if ((patchTuitionAdminUploadEeFlag as any).fulfilled.match(action)) {
      if (searchParams) {
        const apiParamsForRefresh = { ...(typeof searchParams === 'object' && !Array.isArray(searchParams) ? searchParams : { applType: 'AT001' }) };
        if (apiParamsForRefresh.__rawSearch) delete apiParamsForRefresh.__rawSearch;
        await dispatch(searchTuitionApplications(apiParamsForRefresh) as any);
      }
      // Reset dropdowns on success
      setSelectedAction("Select action");
      setSelectedFilterValue("Select filter value");
      setShowFilterDropdown(false);
      // Clear selected checkboxes
      setCheckedRows([]);
      setClearSelectionTrigger((prev) => prev + 1);
  // Ask details tabs (Documents) to refresh
  setDocumentsRefreshTrigger((prev) => prev + 1);
      // Refresh documents for the currently selected application (if available)
      const applicationIdForRefresh = selectedApplicationData?.applictnId ?? selectedApplicationData?.applicationId;
      const nuidForRefresh = selectedApplicationData?.employeeNuId ?? selectedApplicationData?.nuid ?? selectedApplicationData?.empNUID;
      if (applicationIdForRefresh && nuidForRefresh) {
        await dispatch(
          getTuitionAdminDocuments({
            applicationId: String(applicationIdForRefresh),
            nuid: String(nuidForRefresh),
          }) as any 
        );
      }
      setSuccessNotification("EE flag updated for selected applications.");
    } else {
      setErrorNotification("No documents found to update for the given application IDs.");
    }
  };

  const handleApplyAction = async () => {
    if (selectedAction === "Change status") {
      await applyChangeStatus();
    } else if (selectedAction === "EE Upload") {
      await applyEeUpload();
    } else if (selectedAction === "Send email to") {
      handleApplyEmailAction();
    }
  };


  return (
    <div className="lg:mx-40! mx-5!">
       {/* Show loader when fetching active status options */}
      {isFetchingActiveStatuses && <Loader />}
      {/* Scoped styles to ensure headers don't wrap and chevrons sit inline. Avoid forcing flex on TH to prevent layout distortion. */}
      <style>{`
  /* Base header behavior */

   .tuition-dropdown-wide .ds-dropdown__menu-list,
    .tuition-dropdown-wide .drop-menu__list,
    .tuition-dropdown-wide .drop-menu__items {
        left: 0 !important;
        right: auto !important;
        box-sizing: border-box !important;
        max-height: 1000px !important;
        overflow-y: auto !important;
        position: absolute !important;
    }
    th,
    [role="columnheader"],
    .ds-table__header,
    .table-header,
    .header,
    .column-header {
      white-space: nowrap;
      vertical-align: middle;
    }

  /* Apply inline layout ONLY to headers after the first column */
    th:not(:first-child) > *,
    [role="columnheader"]:not(:first-child) > *,
    .ds-table__header:not(:first-child) > *,
    .table-header:not(:first-child) > *,
    .header:not(:first-child) > *,
    .column-header:not(:first-child) > * {
      display: inline-block;
      vertical-align: middle;
    }

    /* Chevron placement */
    .drop-down-menu-chevron {
      display: inline-block;
      margin-right: 10px;
      width: 12px;
      height: 12px;
      vertical-align: middle;
      cursor: pointer;
    }
 

    /* Down chevron gets larger size */
    .drop-down-menu-chevron[src$="/assets/images/chevrondown.svg"] {
      width: 16px;
      height: 16px;
    }
      `}</style>

      {/* Breadcrumbs */}
      <BreadCrumbs
        previousPage="Workplace development"
        currentPage="Tuition reimbursement"
        onPreviousClick={handlePreviousClick}
      />

      {/* Page Header */}
      <h1 className="h1 mt-8">Tuition reimbursement</h1>
      <p className="p3 -book mb-8">
        Manage and track reimbursement applications
      </p>

      {/* Create New Application button - Hidden when email form is shown */}
      {!showEmailForm && (
        <div className="mt-6 flex justify-end">
          <button className="button -primary" onClick={handleCreateNewApplication}>
              <img
                src="/assets/images/plus-circle-outline.svg"
                alt="plus icon"
                className="!w-[1.6rem] !h-[1.25rem] inline-block align-middle mr-2"
                aria-hidden="true"
              />
              {''}
            Create new application
          </button>
        </div>
      )}

      {/* Manage applications title - Hidden when email form is shown */}
      {!showEmailForm && <h2 className="mt-12">Manage applications</h2>}

      {/* Top Action Buttons - Hidden when email form is shown */}
      {showResults && !showEmailForm && (
        <div className="flex gap-4 mb-6 mt-4">
          <button className="button -secondary" onClick={handleShowSearch}>
            <img
              src="/assets/images/search-outline.svg"
              alt="search icon"
              className="!w-[1.6rem] !h-[1.25rem] inline-block align-middle mr-2"
              aria-hidden="true"
            />
            {''}
            Search applications
          </button>
        </div>
      )}

      {/* Success Notification */}
      {successNotification && !showEmailForm && (
        <div className="!mt-4">
          <Notifications messageInfo={successNotification} status={true} />
        </div>
      )}

      {/* Error Notification */}
      {errorNotification && !showEmailForm && (
        <div className="!mt-4">
          <Notifications messageInfo={errorNotification} status={false} />
        </div>
      )}

      {/* Manager Accepted Failure Banner - Targeted for Manager Approval Validation */}
      {warningMessage && failedApplications.length > 0 && !showEmailForm && (
        <div
          className="ds-banner !bg-[#FFFFFF] !border-[#D3D6DC] ds-banner__bg--pebble ds-banner--icon !mb-6 !mt-4"
          data-ds-variant="basic"
          data-ds-version="1"
          data-ds-theme="vessel"
          aria-labelledby="warning-title"
        >
          <div className="ds-banner__icon-img">
            <img src="/assets/images/alertSolid.svg" alt="alert-icon" />
          </div>
          <div className="ds-banner__container">
            <button
              className="ds-banner__close"
              aria-label="close"
              onClick={() => {
                setWarningMessage("");
                setFailedApplications([]);
              }}
            >
              <img src="/assets/images/closeIcon.svg" alt="close-icon" />
            </button>
            <div className="ds-banner__main">
              <div
                className="ds-banner__main-header--announcement"
                id="warning-title"
              >
                {warningMessage}
              </div>
              <div className="ds-banner__main-body">
                [{failedApplications.join(", ")}]. Make sure at least one course per each application has Ready for Payment check box checked under Reimbursement tab below.
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Manager Accepted Failure Banner - Targeted for Manager Approval Validation */}
      {managerAcceptedFailures.length > 0 && !showEmailForm && (
        <div
          className="ds-banner !bg-[#FFFFFF] !border-[#D3D6DC] ds-banner__bg--pebble ds-banner--icon !mb-6 !mt-4"
          data-ds-variant="basic"
          data-ds-version="1"
          data-ds-theme="vessel"
          aria-labelledby="manager-accepted-title"
        >
          <div className="ds-banner__icon-img">
            <img src="/assets/images/alertSolid.svg" alt="alert-icon" />
          </div>
          <div className="ds-banner__container">
            <button
              className="ds-banner__close"
              aria-label="close"
              onClick={() => {
                setManagerAcceptedFailures([]);
              }}
            >
              <img src="/assets/images/closeIcon.svg" alt="close-icon" />
            </button>
            <div className="ds-banner__main">
              <div
                className="ds-banner__main-header--announcement"
                id="manager-accepted-title"
              >
                Manager Approval Required
              </div>
              <div className="ds-banner__main-body">
                <p className="mb-2">
                  The following application(s) cannot be processed for payment because they have not yet been approved by a manager. Please ensure a "Manager Accepted" comment exists before attempting to update the status to "Payment Processed".
                </p>
                <ul className="list-disc pl-5">
                  {managerAcceptedFailures.map((item) => (
                    <li key={item.applicationId}>
                      Application ID {item.applicationId}: {item.reason}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Dropdowns and action buttons - Hidden when email form is shown */}
      {showResults && !showEmailForm && (
        <>
  
        </>
      )}
      
      {/* Search Panel */}
      {showSearchPanel && (
        <TuitionSearchStatus
          selectedStatus={selectedStatus}
          onStatusChange={handleStatusChange}
          onViewResults={handleViewResults}
          initialSearch={searchParams?.__rawSearch}
        />
      )}

   
      {/* Results Table */}
      {showResults && (
        <>
                <hr className="-variation-1" />
          
                {/* Error Banner */}
                {actionError && !showEmailForm && (
                  <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded mb-4 mt-4 flex items-center gap-2">
                    <img
                      src="/assets/images/errorCircleSmall.svg"
                      alt="Error icon"
                      className="!w-[16px] !h-[16px]"
                    />
                    <span>{actionError}</span>
                  </div>
                )}
                
                {/* Dropdowns and action buttons - Hidden when email form is shown */}
                {!showEmailForm && (
                <div className="flex gap-4 mb-6 mt-4 items-end">
                  {/* Wrapper div to disable all child elements when no checkboxes are selected */}
                  <div 
                    className="flex gap-4 items-end"
                    style={{
                      opacity: checkedRows.length === 0 ? 0.5 : 1,
                      pointerEvents: checkedRows.length === 0 ? 'none' : 'auto',
                      cursor: checkedRows.length === 0 ? 'not-allowed' : 'default'
                    }}
                  >
                    <div className="flex flex-col">
                      <Dropdown
                        label="Choose action type"
                        selectedValue={selectedAction}
                        options={[
                          "Select action",
                          "Change status",
                          "Send email to",
                          "EE Upload"
                        ]}
                        onSelect={handleActionSelect}
                        placeholder="Select action"
                        className="tuition-container-dropdown tuition-dropdown-wide w-full"
                      />
                    </div>
                    {showFilterDropdown && (
                      <>
                        <div className="flex flex-col">
                          <Dropdown
                            label="Choose action"
                            selectedValue={selectedFilterValue}
                            options={getActionDropdownOptions()}
                            onSelect={handleFilterValueSelect}
                            placeholder="Select filter value"
                            className="tuition-container-dropdown tuition-dropdown-wide w-full"
                          />
                        </div>
                        <button 
                          className="button -primary" 
                          style={{ marginBottom: '5px' }}
                          onClick={() => {
                            trackButtonClick('Apply action', 'tuition-admin-container', {
                              selectedAction,
                              selectedFilterValue,
                              checkedRowIds: checkedRowIds.length
                            });
                            handleApplyAction();
                          }}
                          disabled={selectedAction === "Select action" || selectedFilterValue === "Select filter value"}
                        >
                          Apply action
                        </button>
                      </>
                    )}
                    {/* Show Apply action button for EE Upload without the dropdown */}
                    {selectedAction === "EE Upload" && (
                      <button 
                        className="button -primary" 
                        style={{ marginBottom: '5px' }}
                        onClick={() => {
                          trackButtonClick('Apply action', 'tuition-admin-container', {
                            selectedAction,
                            checkedRowIds: checkedRowIds.length
                          });
                          handleApplyAction();
                        }}
                      >
                        Apply action
                      </button>
                    )}
                  </div>
                </div>
                )}
        <div className="details-table-wrapper" ref={detailsTableWrapperRef}>
        {isSorting && (
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.6)', zIndex: 10 }}>
              <Loader />
            </div>
          </div>
        )}
         <DetailsTable
           searchResults={searchResults}
           onViewDetails={handleViewDetails}
           onCheckedRowsChange={handleCheckedRowsChange}
           searchParams={searchParams}
           clearSelectionTrigger={clearSelectionTrigger}
           showEmailForm={showEmailForm}
           selectedFilterValue={selectedFilterValue}
           checkedRowIds={checkedRowIds}
           onCancelEmail={handleCancelEmail}
           emailOptions={emailOptions}
           applicationData={selectedApplicationData}
         />
         </div>
        </>
      )}

      {/* Details Tabs */}
      {selectedApplicationData && (
        <>
          <hr className="-variation-1" />
          <div ref={detailsTabsRef}>
          <DetailsTabsContainer applicationData={selectedApplicationData} documentsRefreshTrigger={documentsRefreshTrigger} />
          </div>
        </>
      )}

    </div>
  );
};

export default TuitionContainer;